import { Lightning, Storage } from "@lightningjs/sdk";
import { SettingsItemText } from "../layout/SettingsItemText";
import { SettingsPinContent } from "../layout/SettingsPinContent";
import { STORAGE_KEYS } from "../../../../../lib/utils";
import { isPostTvLinux, isPostTvAndroid } from "../../../../../lib/utils";
import { postTvWebSocket } from "../../../../../lib/websocket";
import { updateParentalPin } from "../../../../../api/mutations";
import { refreshUserData } from "../../../../../lib/utils";

interface SettingsParentalControlTemplateSpec extends Lightning.Component.TemplateSpec {
    Layout: typeof SettingsItemText;
}

export class SettingsParentalControl
    extends Lightning.Component<SettingsParentalControlTemplateSpec>
    implements Lightning.Component.ImplementTemplateSpec<SettingsParentalControlTemplateSpec>
{
    readonly SettingsItem = this.getByRef("Layout")!;

    static override _template(): Lightning.Component.Template<SettingsParentalControlTemplateSpec> {
        return {
            collision: true,
            zIndex: 10,
            Layout: {
                type: SettingsItemText,
                title: "settings.pinCode",
                content: SettingsPinContent
            }
        };
    }

    override _getFocused() {
        return this.SettingsItem;
    }

    override _focus() {
        // Initialize the right panel content when item is focused
        const pinContent = this._getPinContent();
        if (pinContent) {
            if (this._hasPin()) {
                pinContent.open(undefined, undefined, "verify");
            } else {
                pinContent.open(undefined, undefined, "set");
            }
        }
    }

    override _captureKey(e: KeyboardEvent) {
        // When the left label is focused (not the right panel), capture Enter/Right to open PIN flow
        if (!(this.SettingsItem as any)._focusList) {
            if (e.key === "Enter" || e.keyCode === 13 || e.key === "ArrowRight") {
                this._openPinInput();
                return true;
            }
        }
        return false;
    }

    _handleHover() {
        this.fireAncestors("$listItemHover", this);
    }

    _hasPin(): boolean {
        const user = Storage.get(STORAGE_KEYS.user) as any;
        return !!(user?.settings?.parentalPin) || !!Storage.get(STORAGE_KEYS.parentalPin);
    }

    _getPinContent(): any {
        return (this.SettingsItem as any).CustomContent?.children?.[0];
    }

    $unfocusContent() {
        (this.SettingsItem as any).unfocusContent();
    }

    _openPinInput() {
        const pinContent = this._getPinContent();
        if (!pinContent) return;

        if (this._hasPin()) {
            pinContent.open(
                () => {
                    this._setNewPin();
                },
                undefined,
                "verify"
            );
        } else {
            pinContent.open(
                undefined,
                undefined,
                "set",
                (pin: string) => {
                    this._savePin(pin);
                }
            );
        }

        (this.SettingsItem as any).focusContent();
    }

    _setNewPin() {
        const pinContent = this._getPinContent();
        if (!pinContent) return;

        setTimeout(() => {
            pinContent.open(
                () => {
                    console.log("[SettingsParentalControl] PIN set successfully");
                    pinContent._showSuccess("pin.changeSuccess");
                },
                undefined,
                "set",
                (pin: string) => {
                    this._savePin(pin);
                }
            );
        }, 100);
    }

    async _savePin(pin: string) {
        try {
            await updateParentalPin(pin);
            await refreshUserData();
            Storage.set(STORAGE_KEYS.parentalPin, pin);

            if (isPostTvLinux() && postTvWebSocket.isConnected) {
                try {
                    postTvWebSocket.setSettings("pics.pin", pin, () => {});
                } catch (e) {
                    console.error("[SettingsParentalControl] Error saving PIN via WebSocket:", e);
                }
            }

            if (isPostTvAndroid()) {
                try {
                    const android = window.Android;
                    // @ts-ignore
                    if (android && typeof android.setPinCode === "function") {
                        // @ts-ignore
                        android.setPinCode(pin);
                    }
                } catch (e) {
                    console.error("[SettingsParentalControl] Error saving PIN via Android API:", e);
                }
            }
        } catch (error) {
            console.error("[SettingsParentalControl] Error saving PIN via API:", error);
            Storage.set(STORAGE_KEYS.parentalPin, pin);
        }
    }
}
